import { ArrowLeft, Check, Loader2, Pencil } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useParams } from "react-router";
import {
  INCIDENT_SEVERITY_LABELS,
  WEATHER_LABELS,
  formatMissingForSignOff,
  missingForSignOff,
  type JournalEntry,
} from "@gb/contracts";
import { ContentColumn } from "@/app/app-shell";
import { useProjectContext } from "@/app/project-context";
import { SectionLabel } from "@/components/gb/section-label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useApproveJournalEntry, useJournalEntry } from "./journals-api";
import { JournalPhotos } from "./journal-photos";
import { formatJournalDate } from "./journal-screen";
import { SEVERITY_STYLES } from "./incidents";

/** One day's record. `new` and `:entryId/edit` are separate routes. */
export function JournalEntryScreen() {
  const { entryId } = useParams();
  const { projectId } = useProjectContext();
  return <JournalEntryDetail projectId={projectId} entryId={entryId} />;
}

function JournalEntryDetail({
  projectId,
  entryId,
}: {
  projectId: string;
  entryId: string | undefined;
}) {
  const { data: entry, isPending, isError, error, refetch } = useJournalEntry(projectId, entryId);
  const approve = useApproveJournalEntry(projectId);

  if (isPending) {
    return (
      <ContentColumn>
        <div className="flex items-center gap-2 py-16 text-muted" role="status" aria-live="polite">
          <Loader2 size={16} className="animate-spin" />
          <span className="text-[0.9rem]">Loading the day’s record…</span>
        </div>
      </ContentColumn>
    );
  }

  if (isError) {
    return (
      <ContentColumn>
        <BackLink projectId={projectId} />
        <div className="mt-4 rounded-card-lg border-[1.5px] border-danger/40 bg-danger-bg px-6 py-5">
          <p className="text-[0.92rem] font-semibold text-danger">
            Couldn’t load this entry.
          </p>
          <p className="mt-1 text-[0.85rem] text-ink-slate">
            {error instanceof Error ? error.message : "Something went wrong."}
          </p>
          <Button variant="secondary" size="sm" className="mt-4" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      </ContentColumn>
    );
  }

  // The same rule the endpoint enforces, from the same function — so the button
  // and the 400 can never disagree, and a disabled control can say *why*.
  const missing = missingForSignOff(entry);

  return (
    <ContentColumn>
      <BackLink projectId={projectId} />

      <header className="mt-4 mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-[1.7rem] font-bold uppercase leading-[1.05] tracking-[0.01em] text-ink">
            {formatJournalDate(entry.date)}
          </h1>
          <p className="mt-1 text-[0.95rem] text-muted">
            {/* A day can be opened by dropping photographs on it, so these are
                genuinely unknown until somebody writes the record. "Headcount
                not recorded" is the truth; "0 on site" would be a claim. */}
            {entry.headcount === null ? "Headcount not recorded" : `${entry.headcount} on site`}
            {" · "}
            {entry.weather === null ? "weather not recorded" : WEATHER_LABELS[entry.weather]}
            {entry.approved ? " · signed off" : " · not yet signed off"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="secondary" size="sm">
            <Link to={`/projects/${projectId}/journal/${entry.id}/edit`}>
              <Pencil size={15} />
              Edit
            </Link>
          </Button>
          {!entry.approved && (
            <Button
              size="sm"
              className="hover:bg-brand-deep"
              disabled={approve.isPending || missing.length > 0}
              title={
                missing.length > 0
                  ? `Still needs ${formatMissingForSignOff(missing)}.`
                  : undefined
              }
              onClick={() => approve.mutate({ entryId: entry.id })}
            >
              <Check size={15} />
              {approve.isPending ? "Signing off…" : "Sign off"}
            </Button>
          )}
        </div>
      </header>

      {!entry.approved && missing.length > 0 && (
        <p className="mb-4 rounded-input border-[1.5px] border-warn/40 bg-warn/8 px-4 py-2.5 text-[0.85rem] text-ink-prose">
          This day can’t be signed off yet — it still needs{" "}
          <span className="font-semibold">{formatMissingForSignOff(missing)}</span>.{" "}
          <Link
            to={`/projects/${projectId}/journal/${entry.id}/edit`}
            className="font-semibold text-brand underline underline-offset-2"
          >
            Fill it in
          </Link>
          .
        </p>
      )}

      {approve.error && (
        <p role="alert" className="mb-4 text-[0.85rem] font-semibold text-danger">
          {approve.error.message}
        </p>
      )}

      <div className="flex flex-col gap-6">
        {/* First, not last. Photographs are what a site day actually consists
            of, and burying them under seven prose blocks would put the evidence
            below the summary of it. */}
        <Block label="Photos">
          <JournalPhotos
            projectId={projectId}
            entryId={entry.id}
            readOnly={entry.approved}
          />
        </Block>

        {entry.workCompleted && (
          <Block label="Work completed">
            <p className="text-[0.95rem] leading-[1.6] text-ink-prose">{entry.workCompleted}</p>
          </Block>
        )}

        {entry.officeNotes && (
          <Block label="Notes">
            {/* The seed writes these as blank-line-separated paragraphs. */}
            <div className="flex flex-col gap-3">
              {entry.officeNotes.split("\n\n").map((para, i) => (
                <p key={i} className="text-[0.95rem] leading-[1.6] text-ink-prose">
                  {para}
                </p>
              ))}
            </div>
          </Block>
        )}

        {entry.deliveries.length > 0 && (
          <Block label="Deliveries">
            <ul className="flex flex-col gap-2">
              {entry.deliveries.map((d, i) => (
                <li key={i} className="flex flex-wrap items-baseline gap-x-2 text-[0.92rem]">
                  <span className="font-semibold text-ink">{d.supplier}</span>
                  <span className="text-ink-prose">{d.item}</span>
                  <span className="text-muted">× {d.qty}</span>
                  {d.condition !== "ok" && (
                    <span className="font-semibold text-danger">
                      {d.condition === "damaged" ? "damaged" : "wrong spec"}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </Block>
        )}

        {entry.incidents.length > 0 && (
          <Block label="Incidents">
            <ul className="flex flex-col gap-2.5">
              {entry.incidents.map((incident, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span
                    className={cn(
                      "mt-[2px] shrink-0 rounded-full border px-2.5 py-[3px] font-display text-[0.66rem] font-semibold tracking-[0.06em] uppercase",
                      SEVERITY_STYLES[incident.severity],
                    )}
                  >
                    {INCIDENT_SEVERITY_LABELS[incident.severity]}
                  </span>
                  <span className="text-[0.92rem] leading-[1.5] text-ink-prose">
                    {incident.text}
                  </span>
                </li>
              ))}
            </ul>
          </Block>
        )}

        {entry.toolboxTalks && entry.toolboxTalks.length > 0 && (
          <Block label="Toolbox talks">
            <ul className="flex flex-col gap-2">
              {entry.toolboxTalks.map((t, i) => (
                <li key={i} className="text-[0.92rem] text-ink-prose">
                  <span className="font-semibold text-ink">{t.topic}</span>
                  <span className="text-muted">
                    {" "}
                    — {t.durationMin} min, {t.attendees} attended
                    {t.ledBy ? `, led by ${t.ledBy}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </Block>
        )}

        {entry.inspections && entry.inspections.length > 0 && (
          <Block label="Inspections">
            <ul className="flex flex-col gap-3">
              {entry.inspections.map((inspection, i) => (
                <li key={i}>
                  <div className="text-[0.92rem] font-semibold text-ink">
                    {inspection.area}
                    <span
                      className={cn(
                        "ml-2 font-normal",
                        inspection.result === "fail" ? "text-danger" : "text-ok",
                      )}
                    >
                      {inspection.result.replace(/-/g, " ")}
                    </span>
                  </div>
                  <div className="text-[0.85rem] text-muted">{inspection.inspector}</div>
                  {inspection.notes && (
                    <p className="mt-1 text-[0.9rem] leading-[1.5] text-ink-prose">
                      {inspection.notes}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </Block>
        )}

        {entry.visitors && entry.visitors.length > 0 && (
          <Block label="Visitors">
            <ul className="flex flex-col gap-2">
              {entry.visitors.map((v, i) => (
                <li key={i} className="text-[0.92rem] text-ink-prose">
                  <span className="font-semibold text-ink">{v.name}</span>
                  {v.organisation ? `, ${v.organisation}` : ""} — {v.purpose}{" "}
                  <span className="text-muted">
                    ({v.arrived}–{v.departed})
                  </span>
                </li>
              ))}
            </ul>
          </Block>
        )}

        {entry.planVsActual && entry.planVsActual.length > 0 && (
          <Block label="Plan vs actual">
            <ul className="flex flex-col gap-3">
              {entry.planVsActual.map((row, i) => (
                <li key={i} className="grid gap-1 sm:grid-cols-3 sm:gap-4">
                  <span className="text-[0.9rem] text-muted">{row.planned}</span>
                  <span className="text-[0.9rem] text-ink-prose">{row.actual}</span>
                  <span className="text-[0.9rem] font-semibold text-ink">{row.variance}</span>
                </li>
              ))}
            </ul>
          </Block>
        )}

        {entry.sentTo.length > 0 && (
          <Block label="Sent to">
            <p className="text-[0.92rem] text-ink-prose">{entry.sentTo.join(", ")}</p>
          </Block>
        )}
      </div>
    </ContentColumn>
  );
}

function BackLink({ projectId }: { projectId: string }) {
  return (
    <Link
      to={`/projects/${projectId}/journal`}
      className="inline-flex items-center gap-1.5 text-[0.85rem] font-semibold text-muted transition-colors hover:text-ink"
    >
      <ArrowLeft size={15} />
      All entries
    </Link>
  );
}

function Block({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section>
      <SectionLabel>{label}</SectionLabel>
      <div className="mt-2.5 rounded-card-lg border-[1.5px] border-border bg-surface p-5">
        {children}
      </div>
    </section>
  );
}

export type { JournalEntry };
